import {createContext, useContext, useMemo, type ReactNode} from "react";

/**
 * Minimal viewer identity shared with search result cards so they can resolve
 * per-viewer follow/star state (client-side Firestore subscriptions) and route
 * anonymous follow/star clicks to sign-up.
 *
 * `uid` is `null` for anonymous / logged-out viewers. `authResolved` flips true
 * once Firebase auth has reported its initial state, so cards can avoid
 * flickering the wrong button state before auth is known.
 */
export interface Viewer {
    readonly uid: string | null;
    readonly authResolved: boolean;
}

const ViewerContext = createContext<Viewer>({uid: null, authResolved: false});

export function ViewerProvider(props: {uid: string | null; authResolved: boolean; children: ReactNode}) {
    const {uid, authResolved, children} = props;
    const value = useMemo<Viewer>(() => ({uid, authResolved}), [uid, authResolved]);
    return <ViewerContext.Provider value={value}>{children}</ViewerContext.Provider>;
}

export function useViewer(): Viewer {
    return useContext(ViewerContext);
}
